import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API_ENDPOINTS } from "../../api/config";
import JSLayout from "./js_layout";
import './css/job_seeker_dashboard.css';
import './css/employer_search.css';

export default function EmployerSearch() {
  const navigate = useNavigate();
  
  const [counties, setCounties] = useState([]);       
  const [businessTypes, setBusinessTypes] = useState([]); 
  
  const [searchTerm, setSearchTerm] = useState("");
  const [location, setLocation] = useState("");
  const [industry, setIndustry] = useState("");
  
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading] = useState(true);

  const executeSearch = async (currentSearch, currentLocation, currentIndustry) => {
    setLoading(true);
    const token = localStorage.getItem("token");
    
    const queryParams = new URLSearchParams({
      search: currentSearch,
      location: currentLocation,
      industry: currentIndustry
    }).toString();

    try {
      const response = await fetch(`${API_ENDPOINTS.EMPLOYERSEARCH}?${queryParams}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setEmployers(result || []);
      }
    } catch (err) {
      console.error("Search operations error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Form submit wrapper
  const handleSearch = (e) => {
    if (e) e.preventDefault();
    executeSearch(searchTerm, location, industry);
  };

  // Run data setup and initial data lookup once on component mount
  useEffect(() => {
    const fetchAllDataAndInitialSearch = async () => {
      try {
        // 1. Grab your application dropdown metadata in parallel
        const [countiesRes, typesRes] = await Promise.all([
          fetch(`${API_ENDPOINTS.DIMENSIONS}/counties`), 
          fetch(`${API_ENDPOINTS.DIMENSIONS}/business-types`) 
        ]);

        if (countiesRes.ok) {
          const countiesData = await countiesRes.json();
          setCounties(countiesData);
        }
        
        if (typesRes.ok) {
          const typesData = await typesRes.json();
          setBusinessTypes(typesData); 
        }
      } catch (error) {
        console.error("Error setting up profile lookups:", error);
      }

      // 2. Fire immediate fallback search to show all employers initially
      // Pass empty strings explicitly to guarantee pristine initialization
      await executeSearch("", "", "");
    };

    fetchAllDataAndInitialSearch();
  }, []);

  // Quick reset handler for the empty results button
  const handleClearFilters = () => {
    setSearchTerm("");
    setLocation("");
    setIndustry("");
    executeSearch("", "", ""); // Trigger dynamic lookup update
  };

  return (
    <JSLayout title="Discover" titleEm="Employers ✦">
      <div className="jsd-fade">
        
        {/* Search Filtration Input Box */}
        <div className="search-filter-card">
          <form onSubmit={handleSearch} className="search-filter-form">
            <div className="filter-input-group">
              <label>Keyword</label>
              <input 
                type="text" 
                placeholder="Company name or keyword..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="filter-input-group">
              <label>Location</label>
              <select value={location} onChange={(e) => setLocation(e.target.value)}>
                <option value="">All Locations</option>
                {counties.map(countyObj => (
                  <option key={countyObj.id} value={countyObj.name}>{countyObj.name}</option>
                ))}
              </select>
            </div>

            <div className="filter-input-group">
              <label>Industry</label>
              <select value={industry} onChange={(e) => setIndustry(e.target.value)}>
                <option value="">All Industries</option>
                {businessTypes.map(typeString => (
                  <option key={typeString} value={typeString}>{typeString}</option>
                ))}
              </select>
            </div>

            <button type="submit" className="search-action-btn">
              Search Companies
            </button>
          </form>
        </div>

        {/* Results Overview Layout Section */}
        <div className="jsd-section-hdr" style={{ marginTop: "2rem" }}>
          <div className="jsd-section-title">Search <em>results</em></div>
          <span className="results-count-label">{employers.length} companies discovered</span>
        </div>

        {loading ? (
          <div className="search-loader-container">Gathering company records...</div>
        ) : (
          <div className="search-results-grid">
            {employers.length > 0 ? (
              employers.map((company) => (
                <div 
                  key={company.id} 
                  className="jsd-job-card company-card" 
                  onClick={() => navigate(`/employer-details/${company.id}`)}
                >
                  <div className="jsd-job-info">
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <span style={{ fontSize: "1.25rem" }}>🏢</span>
                      <div className="jsd-job-title">{company.name}</div>
                    </div>
                    <div className="jsd-job-company" style={{ marginTop: "4px" }}>
                      Sector: {company.industry || "General Industry"}
                    </div>
                    <div className="jsd-job-meta" style={{ marginTop: "8px" }}>
                      <span>📍 {company.location || "Nairobi"}</span>
                      <span className="jsd-dot">•</span>
                      <span>{company.activeJobsCount || 0} Open Positions</span>
                    </div>
                  </div>
                  
                  <div className="jsd-job-right">
                    <span className="bl-status-pill status-applied" style={{ textTransform: "none" }}>
                      View Profile
                    </span>
                    <div className="jsd-view-link" style={{ marginTop: "10px" }}>Explore jobs →</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="emd-empty-container">
                <div className="emd-empty-content">
                  <div className="emd-empty-icon">🔍</div>
                  <p className="emd-empty-title">No employers found</p>
                  <p className="emd-empty-text">We couldn't find matches for your current filters. Try resetting terms.</p>
                  <button 
                    className="jsd-banner-btn" 
                    onClick={handleClearFilters}
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </JSLayout>
  );
}